//@ts-check

/**
 * @typedef {Object} CustomerBuysCollection
 * @property {string} handle
 * @property {string} id
 * @property {number} productCount
 * @property {string} title
 */

/**
 * @typedef {Object} CustomerBuysProductVariant
 * @property {string} id
 * @property {string} price
 */

/**
 * @typedef {Object} CustomerBuysProduct
 * @property {string} handle
 * @property {string} id
 * @property {string} title
 * @property {CustomerBuysProductVariant[]} variants
 * @property {number} variantCount
 */

/**
 * @typedef {Object} CustomerBuysConfig
 * @property {string} amount
 * @property {CustomerBuysCollection[]} collections
 * @property {'MinimumPurchase' | 'MinimumQuantity'} discountType
 * @property {'Collection' | 'Product'} itemType
 * @property {CustomerBuysProduct[]} products
 * @property {string} quantity
 * @property {string} query
 */

/**
 * The campaign JSON rendered into `<script data-gift-with-purchase-data>`.
 * Only `campaignId` is guaranteed, since `data` falls back to
 * `{ campaignId: null }` when no campaign is active, so every other field is
 * optional.
 *
 * @typedef {Object} GwpCampaign
 * @property {string|null} campaignId
 * @property {boolean} [hiddenFromTheme]
 * @property {boolean} [applyCodeAutomatically]
 * @property {boolean} [isActive]
 * @property {string} [discountCode]
 * @property {boolean} [passedThreshold]
 * @property {number|null} [autoAddVariantId]
 * @property {number} [autoAddQuantity]
 * @property {number} [productCount]
 */

const { fetchJSON, getDefaultRequestConfig } = window.BAO.Utils

class GiftWithPurchase {
  constructor () {
    this.onPushCartChange = this.onPushCartChange.bind(this)
    this.onModalClosed = this.onModalClosed.bind(this)
    this.reconcileInFlight = false
    this.queuedState = null
    this.checkoutDisabled = false
    this.restoringDiscount = false
    this.restorePromise = null
    this.checkoutSubmitInFlight = false

    this.installCartDiscountGuard()
    this.installCheckoutGuard()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      this.onPushCartChange,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.GIFT_WITH_PURCHASE.MODAL_CLOSED,
      this.onModalClosed,
    )

    this.init()
  }

  /**
   * Runs the full GWP reconciliation flow (orphan cleanup, shareable-link code
   * application, auto-add/auto-open, discount-code management) against the
   * current cart on page load, so a customer who already qualifies (e.g. a
   * shareable-link code already applied to the cart from a previous visit)
   * gets their gift immediately, without waiting for a subsequent cart change.
   */
  async init () {
    try {
      const state = await fetchJSON('/cart.js', {
        ...getDefaultRequestConfig(),
        method: 'GET'
      })
      await this.reconcile(state)
    } catch (err) {
      console.error('GWP init error:', err)
    }
  }

  /**
   * @param {CustomEvent} event
   */
  async onPushCartChange (event) {
    const state = event.detail.state ?? await fetchJSON(`/cart.js`, {
      ...getDefaultRequestConfig(),
      method: 'GET'
    })

    await this.reconcile(state)
  }

  /**
   * A gift-selection modal was closed. If it belonged to the shareable-link
   * campaign and its gift didn't end up in the cart (the customer dismissed
   * it without picking), decline the offer immediately, via
   * `declineShareableLinkGift`, rather than waiting for the next cart
   * change.
   *
   * @param {CustomEvent} event
   */
  async onModalClosed (event) {
    const campaignId = event.detail?.campaignId
    if (!campaignId) return

    const campaign = this.getShareableLinkCampaignCandidate()
    if (!campaign || campaign.campaignId !== campaignId) return
    if (this.hasItemInCart(this.state, campaignId)) return

    await this.declineShareableLinkGift(campaign)
  }

  /**
   * @param {CartState} state
   */
  async reconcile (state) {
    if (this.reconcileInFlight) {
      this.queuedState = state

      // Whatever raised this change may have repainted the footer, and with it
      // a fresh, enabled button, while the in-flight pass is still settling.
      this.applyCheckoutDisabled()
      return
    }

    this.reconcileInFlight = true
    this.setCheckoutDisabled(true)

    try {
      let next = state

      while (next) {
        this.queuedState = null
        await this.reconcileOnce(next)
        next = this.queuedState
      }
    } finally {
      this.reconcileInFlight = false
      this.setCheckoutDisabled(false)
    }
  }

  /**
   * The checkout button, if the cart footer is currently rendered.
   *
   * @returns {HTMLButtonElement|null}
   */
  get checkoutButton () {
    return /** @type {HTMLButtonElement|null} */ (
      document.querySelector('[data-gift-with-purchase-checkout-button-el]')
    )
  }

  /**
   * Blocks checkout for the duration of a reconcile pass.
   *
   * The cart is only correct once the pass has settled. Between the gift
   * landing in the bag and its discount code being applied it is a round trip
   * short: the gift is on the cart at full price and the subtotal counts it,
   * so checking out in that window would charge for it. The drawer now paints
   * mid-pass rather than staying frozen until the end, which is what makes
   * that window reachable at all.
   *
   * @param {boolean} isDisabled
   */
  setCheckoutDisabled (isDisabled) {
    this.checkoutDisabled = isDisabled
    this.applyCheckoutDisabled()
  }

  /**
   * Re-applies the current state to the button in the DOM. Needed after every
   * render, since each one swaps in a fresh, enabled button from the server.
   */
  applyCheckoutDisabled () {
    const button = this.checkoutButton
    if (!button) return

    button.disabled = this.checkoutDisabled

    if (this.checkoutDisabled) {
      button.setAttribute('aria-disabled', 'true')
      return
    }

    button.removeAttribute('aria-disabled')
  }

  /**
   * @param {CartState} state
   */
  async reconcileOnce (state) {
    const previousState = this.state
    this.state = state

    await this.refreshStaleEmptyCartSections()
    this.syncRemovedGwpItems(previousState, this.state)

    this.saveShareableLinkCode(this.state)
    this.syncForegroundCampaignTracking()
    this.updateActiveCampaignVisibility()

    if (!this.shouldRun(this.state)) {
      if (await this.syncGwpDiscountCodes(this.state) && this.state.item_count > 0) {
        if (this.sectionsAreStale()) {
          this.state = await window.BAO.Cart.getCartWithSections()
        }
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
          state: this.state,
        })
        this.applyCheckoutDisabled()
      }
      return
    }

    let didMutate = await this.removeOrphanedGwpItems(this.state)

    // Keep the shareable-link campaign's per-cycle tracking in sync with its
    // current eligibility. Declines it if its gift was removed after being
    // claimed, and resets its tracking if it's no longer primary at all, so
    // stale state doesn't linger into its next eligibility window.
    await this.syncShareableLinkCampaignState(this.state)

    // Apply stored shareable-link code if criteria just became met and code
    // isn't in the cart yet, then continue this pass as primary.
    if (await this.maybeApplyShareableLinkCode(this.state)) {
      didMutate = true
    }

    this.syncForegroundCampaignTracking()

    if (!this.getCartIsValid()) {
      this.syncForegroundCampaignTracking()
    }

    if (await this.removeIneligibleGwpItems(this.state)) {
      didMutate = true
    }

    // The gift goes in before its discount code, not after, so the customer
    // sees it a whole round trip earlier. The line renders at its campaign
    // price in the meantime (see `get-gift-with-purchase-discounted-price`),
    // so nothing shows a price that is about to correct itself.
    //
    // `shouldApplyCurrentDiscountCode` still resolves to true once the gift is
    // in the cart, via its `getClaimedCampaignId` check rather than its
    // `shouldAutoAddCurrentCampaign` one, so the code is applied either way.
    const alreadyInCart = this.hasItemInCart(this.state, this.data.campaignId)

    if (this.shouldAutoAddCurrentCampaign() && !alreadyInCart) {
      this.trackRemovalOfAutoAddedGwpItem()
      await this.autoAddGwpItem()
      didMutate = true

      this.paintCartItems()
    }

    if (await this.syncGwpDiscountCodes(this.state)) {
      didMutate = true
    }

    // Must stay after the discount sync, or a gift that is briefly at full
    // price is stripped before its code has had a chance to apply.
    if (await this.removeIneligibleGwpItems(this.state, { requireApplicableDiscount: true })) {
      didMutate = true
    }

    // Cart-mutation helpers suppress the automatic DOM section-swap whenever
    // a GWP campaign is configured, so reconcile() must perform it itself.
    // Every mutation above now requests sections directly, so `this.state`
    // already carries fresh section HTML from whichever one ran last (see
    // `replaceCartState`) reflecting the drawer's discounted prices and
    // correct gift label. Only fall back to a fresh combined fetch if that
    // HTML is missing or still shows the pre-mutation empty-bag snapshot.
    if (didMutate && this.sectionsAreStale()) {
      this.state = await window.BAO.Cart.getCartWithSections()
    }

    if (!this.state.sections) return

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
      state: this.state,
    })
    this.applyCheckoutDisabled()
    this.updateActiveCampaignVisibility()
    window.BAO.PushCartGWP?.updateSelectionCounts(this.state)

    this.maybeAutoOpenSelectionModal(this.state, this.getCartIsValid())
  }

  /**
   * @param {CartState} state
   * @param {string} campaignId
   *
   * @returns {boolean}
   */
  hasItemInCart (state, campaignId) {
    return state.items.some(
      (item) =>
        item.properties?._gwp === 'true' &&
        item.properties?._gwp_campaign === campaignId,
    )
  }

  /**
   * A mutation's response can still lack usable `push-cart-items` HTML (a
   * section that failed to render comes back `null`; see `renderSections`).
   * Keep this pass's existing section HTML in that case so later eligibility
   * checks do not fall back to a stale live DOM (e.g. the empty-bag markup
   * still on the page because `preventRender` skipped the drawer swap).
   *
   * @param {CartState|null|undefined} next
   */
  replaceCartState (next) {
    if (!next) return

    const currentSections = this.state?.sections
    const nextHasItemsHtml = typeof next.sections?.['push-cart-items'] === 'string'
    const currentHasItemsHtml = typeof currentSections?.['push-cart-items'] === 'string'

    if (!nextHasItemsHtml && currentHasItemsHtml) {
      next.sections = currentSections
    }

    this.state = next
  }

  async autoAddGwpItem () {
    await this.removeOneMatchingNonGwpItem(this.data.autoAddVariantId)

    // `mergeCart: false` keeps the GET-and-merge off the critical path. It
    // only ever existed because `/cart/add.js` returns the just-added line
    // items rather than `discount_codes` and the full `items` list, and the
    // discount sync that now runs straight after this returns both, from the
    // server. Folding the added lines in locally covers the checks in between
    // and gets the gift on screen a round trip sooner.
    const next = await window.BAO.Cart.addItem({
      id: this.data.autoAddVariantId,
      quantity: this.data.autoAddQuantity,
      properties: {
        _gwp: 'true',
        _gwp_campaign: this.data.campaignId,
      },
    }, { silent: true, mergeCart: false })

    if (Array.isArray(next?.items)) {
      this.mergeAddedItemsIntoState(next)
      return
    }

    // Unexpected response shape, so fall back to the authoritative cart
    // rather than carrying on with a state that is missing the gift.
    const current = await window.BAO.Cart.getCartWithSections()

    if (!this.hasItemInCart(current, this.data.campaignId)) {
      window.BAO.Cart.syncRecommendationHistory(current.items)
    }

    this.replaceCartState(current)
  }

  /**
   * Folds an `/cart/add.js` response, which carries only the lines it just
   * added, into `this.state`. Cart totals are deliberately left as they were:
   * they are only correct once the discount code lands, the mutation that
   * applies it replaces the whole state with the server's, and nothing
   * between here and there reads them.
   *
   * @param {CartState} next
   */
  mergeAddedItemsIntoState (next) {
    const added = next.items ?? []
    const addedCount = added.reduce((total, item) => total + (item.quantity ?? 0), 0)

    this.replaceCartState({
      ...this.state,
      items: [...(this.state?.items ?? []), ...added],
      item_count: (this.state?.item_count ?? 0) + addedCount,
      sections: next.sections,
    })
  }

  /**
   * Swaps just the cart lines into the drawer, part way through a pass.
   *
   * Deliberately not the footer: until the discount code lands,
   * `cart.total_price` still counts the gift at full price, while the line
   * itself already renders at its campaign price. Leaving the footer on its
   * pre-gift subtotal keeps the two consistent, and for a free gift that
   * subtotal is the final one anyway.
   */
  paintCartItems () {
    const html = this.state?.sections?.['push-cart-items']
    if (typeof html !== 'string') return
    if (this.sectionHtmlLooksEmpty(html)) return

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
      state: { sections: { 'push-cart-items': html } },
    })

    this.applyCheckoutDisabled()
    this.updateActiveCampaignVisibility()
  }

  /**
   * If this gift variant is already in the bag as a regular (or formerly
   * converted) line, remove one unit so `/cart/add` can create a new line
   * with `_gwp` properties instead of merging onto the paid item.
   *
   * @param {string|number} variantId
   *
   * @returns {Promise<boolean>}
   */
  async removeOneMatchingNonGwpItem (variantId) {
    const targetId = Number(variantId)
    if (!targetId) return false

    const state = this.state ?? {}
    const existing = (state.items ?? []).find((item) => {
      if (Number(item.variant_id ?? item.id) !== targetId) return false
      return item.properties?._gwp !== 'true'
    })

    if (!existing) return false

    const nextQuantity = existing.quantity - 1
    const next = nextQuantity > 0
      ? await window.BAO.Cart.cartChange({
        id: existing.key,
        quantity: nextQuantity,
        sections: window.BAO.sectionRenderer
          .withoutRecommendations(window.BAO.sectionRenderer.pushCartSections)
          .map((s) => s.section),
        sections_url: window.BAO.Cart.sectionsUrl,
      })
      : await window.BAO.Cart.removeItem(existing.key, {
        silent: true,
        preserveRecommendationSource: true,
      })

    this.replaceCartState(next)
    return true
  }

  /**
   * @param {string} [campaignId]
   */
  trackRemovalOfAutoAddedGwpItem (campaignId = this.data.campaignId) {
    sessionStorage.setItem(`has_removed_auto_added_gwp_${campaignId}`, 'true')
  }

  /**
   * @param {string} [campaignId]
   *
   * @returns {boolean}
   */
  hasRemovedAutoAddedGwpItem (campaignId = this.data.campaignId) {
    return sessionStorage.getItem(`has_removed_auto_added_gwp_${campaignId}`) === 'true'
  }

  /**
   * @param {string} [campaignId]
   */
  clearTrackedStateOfAutoAddedGwpItem (campaignId = this.data.campaignId) {
    sessionStorage.removeItem(`has_removed_auto_added_gwp_${campaignId}`)
  }

  /**
   * @param {string} [campaignId]
   */
  trackAutoOpenedGwpModal (campaignId = this.data.campaignId) {
    sessionStorage.setItem(`has_opened_multi_gwp_modal_${campaignId}`, 'true')
  }

  /**
   * @param {string} [campaignId]
   *
   * @returns {boolean}
   */
  hasAutoOpenedGwpModal (campaignId = this.data.campaignId) {
    return sessionStorage.getItem(`has_opened_multi_gwp_modal_${campaignId}`) === 'true'
  }

  /**
   * @param {string} [campaignId]
   */
  clearTrackedStateOfAutoOpenedGwpModal (campaignId = this.data.campaignId) {
    sessionStorage.removeItem(`has_opened_multi_gwp_modal_${campaignId}`)
  }

  /**
   * @param {string} campaignId
   */
  markShareableLinkGiftClaimed (campaignId) {
    sessionStorage.setItem(`has_claimed_shareable_gift_${campaignId}`, 'true')
  }

  /**
   * @param {string} campaignId
   *
   * @returns {boolean}
   */
  hasClaimedShareableLinkGift (campaignId) {
    return sessionStorage.getItem(`has_claimed_shareable_gift_${campaignId}`) === 'true'
  }

  /**
   * @param {string} campaignId
   */
  clearClaimedShareableLinkGift (campaignId) {
    sessionStorage.removeItem(`has_claimed_shareable_gift_${campaignId}`)
  }

  /**
   * Auto-opens the selection modal once per threshold-pass cycle when the
   * active campaign requires it and the customer hasn't yet received their gift.
   *
   * Applies when:
   *   - `applyCodeAutomatically` is true and a choice is needed (multiple
   *     products or variants), regardless of visibility
   *   - `applyCodeAutomatically` is false and the campaign is hidden from theme,
   *     since the modal must open automatically when there is no visible trigger
   *
   * A visible manual campaign never auto-opens. The customer must click the
   * trigger themselves.
   *
   * @param {CartState} state
   * @param {boolean} cartIsValid
   */
  maybeAutoOpenSelectionModal (state, cartIsValid) {
    const hasItemInCampaignCart = this.hasItemInCart(state, this.data.campaignId)
    const hasOpenedThisCycle = this.hasAutoOpenedGwpModal()

    if (!cartIsValid) return
    if (hasItemInCampaignCart) return
    if (hasOpenedThisCycle) return
    if (
      !this.isShareableLinkCampaignPrimary() &&
      this.hasDeclinedForegroundGiftThisCycle()
    ) {
      return
    }

    const applyAutomatically = this.data.applyCodeAutomatically
    const hiddenFromTheme = this.data.hiddenFromTheme

    const shouldAutoOpen =
      (applyAutomatically && this.needsGiftSelection) ||
      (!applyAutomatically && hiddenFromTheme)

    if (!shouldAutoOpen) return

    this.trackAutoOpenedGwpModal()

    window.BAO.dispatchEvent(window.BAO.EVENTS.GIFT_WITH_PURCHASE.OPEN_MODAL, {
      campaignId: this.data.campaignId,
    })
  }

  /**
   * @param {CartState} state
   */
  addDiscountCode (state) {
    return window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
        this.discountCode,
      ].join(','),
    )
  }

  /**
   * @param {CartState} state
   */
  removeDiscountCode (state) {
    return window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter(
          (code) => code !== this.discountCode,
        ),
      ].join(','),
    )
  }

  /**
   * Keeps only the GWP discount code that should be on the cart (the claimed
   * or uniquely auto-addable campaign, or the shareable-link primary). All
   * other campaign codes are stripped so declined / switched offers do not
   * linger as inapplicable codes.
   *
   * @param {CartState} state
   *
   * @returns {Promise<boolean>}
   */
  async syncGwpDiscountCodes (state) {
    const gwpCodes = this.campaigns
      .map((campaign) => campaign.discountCode)
      .filter(Boolean)

    if (gwpCodes.length === 0) return false

    const current = this.getCurrentAppliedDiscountCodes(state)
    const nonGwp = current.filter((code) => !gwpCodes.includes(code))
    const keepGwp = this.shouldApplyCurrentDiscountCode() && this.discountCode
      ? [this.discountCode]
      : []
    const next = [...nonGwp, ...keepGwp]
    const unchanged =
      [...current].sort().join(',') === [...next].sort().join(',')

    if (unchanged) return false

    const newState = await window.BAO.Cart.updateDiscount(next.join(','))
    this.replaceCartState(newState)

    return true
  }

  /**
   * Removes GWP items belonging to campaigns that are no longer the claimed
   * or shareable-link primary. When nothing is claimed, leftover GWP lines
   * are removed so a declined gift cannot pin visibility.
   *
   * @param {CartState} state
   *
   * @returns {Promise<boolean>}
   */
  async removeOrphanedGwpItems (state) {
    const claimedCampaignId = this.getClaimedCampaignId(state)
    const keepCampaignId = this.isShareableLinkCampaignPrimary()
      ? this.data.campaignId
      : claimedCampaignId

    const orphanedItems = state.items.filter((item) => {
      if (item.properties?._gwp !== 'true') return false
      if (!keepCampaignId) return true
      return item.properties?._gwp_campaign !== keepCampaignId
    })

    if (orphanedItems.length === 0) return false

    const next = await window.BAO.Cart.removeItems(
      orphanedItems.map((item) => item.key),
      { skipGwp: true },
    )

    this.replaceCartState(next)
    return true
  }

  /**
   * Removes gift lines whose campaign is no longer live or qualifying.
   * After discount codes have been synced, also removes a gift that is
   * still at full price because its code is not applicable. That second
   * check must not run before the code is applied, or a modal re-add
   * (gift lands at full price for one pass) is stripped immediately.
   *
   * @param {CartState} state
   * @param {{ requireApplicableDiscount?: boolean }} [options]
   *
   * @returns {Promise<boolean>}
   */
  async removeIneligibleGwpItems (state, { requireApplicableDiscount = false } = {}) {
    const items = (state.items ?? []).filter((item) => (
      this.shouldRemoveGwpItem(item, state, { requireApplicableDiscount })
    ))

    if (items.length === 0) return false

    const next = await window.BAO.Cart.removeItems(
      items.map((item) => item.key),
      { skipGwp: true },
    )

    this.replaceCartState(next)
    return true
  }

  /**
   * @param {object} item
   * @param {CartState} state
   * @param {{ requireApplicableDiscount?: boolean }} [options]
   *
   * @returns {boolean}
   */
  shouldRemoveGwpItem (item, state, { requireApplicableDiscount = false } = {}) {
    if (item.properties?._gwp !== 'true') return false

    const campaignId = item.properties._gwp_campaign
    if (this.isShareableLinkCampaignPrimary() && campaignId === this.data.campaignId) {
      return false
    }

    const campaign = this.campaigns.find((entry) => entry.campaignId === campaignId)
    if (!campaign || campaign.isActive === false || !campaign.passedThreshold) return true
    if (!requireApplicableDiscount) return false

    const applied = (state.discount_codes ?? []).find((discount) => discount.code === campaign.discountCode)
    return item.final_price > 0 && (!applied || applied.applicable === false)
  }

  /**
   * @param {CartState} state
   *
   * @returns {boolean}
   */
  getAlreadyHasDiscountCode (state) {
    return state.discount_codes.some((discountCode) => discountCode.code === this.discountCode)
  }

  /**
   * @param {CartState} state
   *
   * @returns {string[]}
   */
  getCurrentAppliedDiscountCodes (state) {
    return (state.discount_codes ?? []).map((discountCode) => discountCode.code)
  }

  // ---------------------------------------------------------------------------
  // Shareable-link code tracking
  // ---------------------------------------------------------------------------

  /**
   * If any hidden-from-theme campaign's discount code is present in
   * `cart.discount_codes`, persist it to session storage so we can re-apply it
   * after cart changes remove it.
   *
   * @param {CartState} state
   */
  saveShareableLinkCode (state) {
    const backgroundCampaigns = this.campaigns.filter((c) => c.hiddenFromTheme === true)
    for (const campaign of backgroundCampaigns) {
      if (!campaign.discountCode) continue
      const isInCart = state.discount_codes.some((d) => d.code === campaign.discountCode)
      if (isInCart) {
        sessionStorage.setItem('gwp_shareable_link_code', campaign.discountCode)
        return
      }
    }
  }

  /**
   * Removes the shareable-link campaign's discount code from the cart, if
   * currently applied, and refreshes `this.state.discount_codes` to match
   * so subsequent checks in this same reconcile pass see it as gone.
   *
   * @param {GwpCampaign} campaign
   */
  async removeShareableLinkDiscountCode (campaign) {
    const isInCart = this.state.discount_codes.some((d) => d.code === campaign.discountCode)
    if (!isInCart) return

    const newState = await window.BAO.Cart.updateDiscount(
      this.getCurrentAppliedDiscountCodes(this.state)
        .filter((code) => code !== campaign.discountCode)
        .join(','),
    )
    this.replaceCartState(newState)
  }

  /**
   * The customer has declined the shareable-link campaign's gift, either by
   * dismissing its selection modal without picking, or by removing a
   * previously-claimed gift from the cart (see
   * `syncShareableLinkCampaignState`). Removes the discount code so
   * eligibility drops immediately and foreground campaigns reappear via the
   * existing `isShareableLinkCampaignPrimary` check, and forgets the stored
   * code so `maybeApplyShareableLinkCode` doesn't silently re-apply it.
   *
   * The customer can only get this gift again by revisiting the shareable
   * link. Shopify re-applies the code server-side on that visit, and this
   * campaign's auto-add/auto-opened-modal tracking is cleared here so that
   * revisit is treated as a brand new opportunity rather than a continuation
   * of the declined one.
   *
   * @param {GwpCampaign} campaign
   */
  async declineShareableLinkGift (campaign) {
    sessionStorage.removeItem('gwp_shareable_link_code')
    this.clearClaimedShareableLinkGift(campaign.campaignId)
    this.clearTrackedStateOfAutoAddedGwpItem(campaign.campaignId)
    this.clearTrackedStateOfAutoOpenedGwpModal(campaign.campaignId)

    await this.removeShareableLinkDiscountCode(campaign)

    this.updateActiveCampaignVisibility()
  }

  /**
   * Keeps the shareable-link campaign's per-eligibility-cycle tracking
   * (auto-add, auto-opened-modal, claimed) in sync with whether it's
   * currently primary:
   *
   *   - Still primary and its gift is in the cart: mark it claimed, so a
   *     later removal can be recognized as a decline.
   *   - Still primary but its gift is missing, having previously been
   *     claimed: the customer removed it from the cart, so decline the offer
   *     (see `declineShareableLinkGift`).
   *   - Not primary at all right now (e.g. the cart dropped below
   *     threshold): reset its tracking. `this.data` never resolves to this
   *     campaign while it isn't primary, so nothing else in `reconcile()`
   *     clears its flags in this case. Leaving them stale would silently
   *     suppress the modal/auto-add, or misread a fresh claim as an
   *     already-declined removal, the next time this campaign regains
   *     eligibility (e.g. by revisiting the shareable link). If its gift had
   *     already been claimed (it's presumably just been removed as orphaned
   *     since this campaign is no longer primary), also remove its discount
   *     code so a dangling, now-unused code doesn't linger in the cart, but
   *     leave the stored shareable-link code alone (unlike a full decline)
   *     so it can still silently re-apply via `maybeApplyShareableLinkCode`
   *     if the cart naturally re-qualifies later without revisiting the
   *     link. A campaign never claimed yet (still working toward its
   *     threshold) is left untouched entirely.
   *
   * @param {CartState} state
   */
  async syncShareableLinkCampaignState (state) {
    const candidate = this.getShareableLinkCampaignCandidate()
    if (!candidate) return

    const campaign = this.getShareableLinkCampaign(state)

    if (!campaign) {
      const wasClaimed = this.hasClaimedShareableLinkGift(candidate.campaignId)

      this.clearTrackedStateOfAutoAddedGwpItem(candidate.campaignId)
      this.clearTrackedStateOfAutoOpenedGwpModal(candidate.campaignId)
      this.clearClaimedShareableLinkGift(candidate.campaignId)

      if (wasClaimed) {
        await this.removeShareableLinkDiscountCode(candidate)
      }
      return
    }

    if (this.hasItemInCart(state, campaign.campaignId)) {
      this.markShareableLinkGiftClaimed(campaign.campaignId)
      return
    }

    if (!this.hasClaimedShareableLinkGift(campaign.campaignId)) return

    await this.declineShareableLinkGift(campaign)
  }

  /**
   * The background campaign matching the shareable-link code stored in
   * session storage, if any, regardless of whether it currently qualifies
   * (active/threshold/in cart). Used to resolve the qualifying campaign and
   * to look up its campaignId even when it no longer qualifies (e.g. to
   * clear its session-tracked state once the cart drops below threshold).
   *
   * @returns {GwpCampaign|null}
   */
  getShareableLinkCampaignCandidate () {
    const code = sessionStorage.getItem('gwp_shareable_link_code')
    if (!code) return null

    return this.campaigns.find(
      (c) => c.hiddenFromTheme === true && c.discountCode === code,
    ) ?? null
  }

  /**
   * Returns the background GWP campaign that should be treated as primary,
   * or null if none qualifies. A campaign qualifies when:
   *   - Its code is stored in session storage (customer arrived via shareable link)
   *   - The campaign is active (not deactivated)
   *   - Its qualifying threshold has been passed
   *   - Its discount code is currently applied to the cart
   *
   * @param {CartState} state
   *
   * @returns {GwpCampaign|null}
   */
  getShareableLinkCampaign (state) {
    const campaign = this.getShareableLinkCampaignCandidate()
    if (!campaign) return null

    // Deactivated GWPs must never become primary
    if (campaign.isActive === false) return null

    // Must have passed qualifying threshold
    if (!campaign.passedThreshold) return null

    // Code must be applied to the cart
    const isInCart = state.discount_codes.some((d) => d.code === campaign.discountCode)
    if (!isInCart) return null

    return campaign
  }

  /**
   * Applies the stored shareable-link code to the cart if:
   *   - A code is in session storage
   *   - The matching campaign is active and its threshold is passed
   *   - The code is not already in the cart
   *
   * Returns true when the code was applied (caller should return early and let
   * the subsequent AFTER_RENDER handle the rest of the flow).
   *
   * @param {CartState} state
   *
   * @returns {Promise<boolean>}
   */
  async maybeApplyShareableLinkCode (state) {
    const campaign = this.getShareableLinkCampaignCandidate()
    if (!campaign) return false

    if (campaign.isActive === false) return false
    if (!campaign.passedThreshold) return false

    const alreadyApplied = state.discount_codes.some((d) => d.code === campaign.discountCode)
    if (alreadyApplied) return false

    const newState = await window.BAO.Cart.updateDiscount(
      [
        ...this.getCurrentAppliedDiscountCodes(state).filter((c) => c !== campaign.discountCode),
        campaign.discountCode,
      ].join(','),
    )
    this.replaceCartState(newState)
    return true
  }

  // ---------------------------------------------------------------------------

  /**
   * @returns {boolean}
   */
  getCartIsValid () {
    return this.data.passedThreshold
  }

  /**
   * @param {CartState} state
   *
   * @returns {boolean}
   */
  shouldRun (state) {
    const sections = state?.sections

    if (typeof sections?.['push-cart-items'] === 'string') {
      const section = new DOMParser().parseFromString(sections['push-cart-items'], 'text/html')
      return !!section.querySelector('script[data-gift-with-purchase-data]')
    }

    // No fresh sections payload on this state, so fall back to what's already
    // rendered in the live DOM instead of assuming nothing is configured.
    return !!document.querySelector('script[data-gift-with-purchase-data]')
  }

  get discountCode () {
    if (!this.shouldRun(this.state)) return null

    return this.data.discountCode
  }

  /**
   * All rendered campaign script tags, in block/DOM order.
   *
   * @returns {HTMLScriptElement[]}
   */
  get dataElements () {
    const sections = this.state?.sections

    if (typeof sections?.['push-cart-items'] === 'string') {
      const section = new DOMParser().parseFromString(sections['push-cart-items'], 'text/html')
      return Array.from(section.querySelectorAll('script[data-gift-with-purchase-data]'))
    }

    // No fresh sections payload (e.g. init()'s plain /cart.js fetch on page
    // load has none), so fall back to what's already rendered in the live DOM.
    return Array.from(document.querySelectorAll('script[data-gift-with-purchase-data]'))
  }

  /**
   * Every rendered campaign, in block/DOM order.
   *
   * @returns {GwpCampaign[]}
   */
  get campaigns () {
    return this.dataElements
      .map((el) => {
        try {
          return JSON.parse(el.textContent.trim())
        } catch (err) {
          return null
        }
      })
      .filter((campaign) => campaign && campaign.campaignId)
  }

  /**
   * The campaignId of the gift the customer has actually claimed, if any (a
   * `_gwp` line item is already in the cart). Until something is claimed,
   * every qualifying (or not-yet-qualifying) campaign stays visible side by
   * side. Only once a claim exists does every other campaign hide, matching
   * the epic's "max one promotional product" constraint.
   *
   * @param {CartState} state
   *
   * @returns {string|null}
   */
  getClaimedCampaignId (state) {
    const claimedItem = state?.items?.find((item) => item.properties?._gwp === 'true')

    return claimedItem?.properties?._gwp_campaign ?? null
  }

  /**
   * Resolves the single visible (not hidden_from_theme) campaign that
   * cart-mutating logic (auto-add, auto-apply-discount-code, orphan cleanup)
   * acts on: the campaign the customer has already claimed a gift for, if any;
   * otherwise the first (by block order) campaign whose threshold has passed;
   * otherwise the first visible campaign at all.
   *
   * Kept as its own step, separate from `data`, so a shareable-link GWP can
   * later override this resolution (shareable-link campaign wins whenever its
   * own criteria is met) without reworking it.
   *
   * @returns {GwpCampaign|null}
   */
  getForegroundActiveCampaign () {
    const promotable = this.getPromotableCampaigns()
    const claimedCampaignId = this.getClaimedCampaignId(this.state)
    const claimedCampaign = promotable.find((campaign) => campaign.campaignId === claimedCampaignId)

    return claimedCampaign ?? this.getEligibleForegroundCampaigns()[0] ?? promotable[0] ?? null
  }

  /**
   * Visible, publishable campaigns in block/DOM order.
   *
   * @returns {GwpCampaign[]}
   */
  getPromotableCampaigns () {
    return this.campaigns.filter(
      (campaign) => !campaign.hiddenFromTheme && campaign.isActive !== false,
    )
  }

  /**
   * Visible campaigns whose threshold has passed, in block/DOM order.
   *
   * @returns {GwpCampaign[]}
   */
  getEligibleForegroundCampaigns () {
    return this.getPromotableCampaigns().filter((campaign) => campaign.passedThreshold)
  }

  /**
   * Auto-add the current campaign when it can be claimed without a choice,
   * including a first add that jumps over several thresholds at once
   * (e.g. quantity 15). Do not auto-add a replacement after the customer
   * has already removed a gift in this eligibility cycle.
   *
   * @returns {boolean}
   */
  shouldAutoAddCurrentCampaign () {
    if (!this.state?.item_count) return false
    if (!this.data.campaignId) return false
    if (!this.data.applyCodeAutomatically || !this.data.autoAddVariantId) return false
    if (this.hasDeclinedForegroundGiftThisCycle()) return false
    if (this.getClaimedCampaignId(this.state)) return false

    if (this.isShareableLinkCampaignPrimary()) return true

    return this.data.passedThreshold === true
  }

  /**
   * True when the customer has already declined or removed any visible
   * campaign's gift this cycle. Used instead of "exactly one eligible
   * campaign" so a first add over the threshold can still auto-add, while
   * removing that gift does not silently claim the next offer.
   *
   * @returns {boolean}
   */
  hasDeclinedForegroundGiftThisCycle () {
    return this.getPromotableCampaigns().some((campaign) => (
      this.hasRemovedAutoAddedGwpItem(campaign.campaignId)
    ))
  }

  /**
   * `/cart/add` section HTML can still be the empty-bag snapshot when the
   * customer adds many units in one go. Eligibility would then stay false
   * and the gift would never auto-add.
   *
   * A state with no `sections` payload at all (e.g. `init()`'s plain
   * `/cart.js` fetch on page load) is never stale — the live DOM is
   * server-rendered and fresh by definition — so only a *present but
   * empty-looking* section HTML counts as stale here.
   *
   * @returns {Promise<boolean>}
   */
  async refreshStaleEmptyCartSections () {
    if (!this.state?.item_count) return false

    const html = this.state.sections?.['push-cart-items']
    if (typeof html !== 'string') return false
    if (!this.sectionHtmlLooksEmpty(html)) return false

    this.replaceCartState(await window.BAO.Cart.getCartWithSections())
    return true
  }

  /**
   * True when `this.state` has a non-empty cart but its sections payload is
   * missing, or still shows the pre-mutation empty-bag snapshot (see
   * `sectionHtmlLooksEmpty`). Checked after this pass's own mutations, all of
   * which now request sections directly (see `replaceCartState`), so a
   * caller can tell whether the last mutation's response is trustworthy for
   * render or whether it must fall back to a fresh combined fetch.
   *
   * @returns {boolean}
   */
  sectionsAreStale () {
    if (!this.state?.item_count) return false

    const html = this.state.sections?.['push-cart-items']
    if (typeof html !== 'string') return true

    return this.sectionHtmlLooksEmpty(html)
  }

  /**
   * @param {string} html
   *
   * @returns {boolean}
   */
  sectionHtmlLooksEmpty (html) {
    const section = new DOMParser().parseFromString(html, 'text/html')
    const isEmptyMarkup = !!section.querySelector('.psh-CartItems_Item-empty')
    const lineCount = section.querySelectorAll('.psh-CartItems_Item:not(.psh-CartItems_Item-empty)').length

    return isEmptyMarkup && lineCount === 0
  }

  /**
   * Marks a gift as declined when the customer removes it while that
   * campaign is still qualifying, so reconcile does not auto-add another.
   *
   * @param {CartState|null|undefined} previousState
   * @param {CartState} nextState
   */
  syncRemovedGwpItems (previousState, nextState) {
    const previousGifts = (previousState?.items ?? []).filter((item) => (
      item.properties?._gwp === 'true'
    ))
    const nextKeys = new Set(
      (nextState?.items ?? [])
        .filter((item) => item.properties?._gwp === 'true')
        .map((item) => item.key),
    )

    for (const item of previousGifts) {
      if (nextKeys.has(item.key)) continue

      const campaignId = item.properties?._gwp_campaign
      const campaign = this.campaigns.find((entry) => entry.campaignId === campaignId)
      if (!campaign?.passedThreshold || campaign.isActive === false) continue

      this.trackRemovalOfAutoAddedGwpItem(campaignId)
    }
  }

  /**
   * Intelligems (`igId`) and Upstack post `{ attributes }` to `/cart/update.js`
   * on a first visit, in parallel with the gift's discount update. That
   * attribute write does not carry the code, so when it lands last the towel
   * is full price again. The drawer does not re-render for those requests, and
   * it prices a `_gwp` line as free from the campaign, so the bag still looks
   * right and checkout is where the missing code shows. A returning browser
   * usually finishes those posts before anything is added.
   *
   * Once a gift line is in the cart, fold its code into attribute updates, and
   * put the code back if one of those responses comes back without it.
   */
  installCartDiscountGuard () {
    if (window.fetch.__gwpDiscountGuard) return

    const originalFetch = window.fetch.bind(window)
    const guardedFetch = async (input, init) => {
      const isUpdate = this.isCartUpdateRequest(input)
      let nextInit = init
      let restoreIfStripped = false

      if (isUpdate && !this.restoringDiscount) {
        const prepared = this.prepareCartUpdate(init)
        nextInit = prepared.init
        restoreIfStripped = prepared.restoreIfStripped
      }

      const response = await originalFetch(input, nextInit)

      if (restoreIfStripped && response.ok) {
        await this.restoreIfResponseStrippedDiscount(response)
      }

      return response
    }

    guardedFetch.__gwpDiscountGuard = true
    window.fetch = guardedFetch
  }

  /**
   * The bag renders the gift at its free price before the code is on the
   * cart. Re-check the code at the moment of checkout, including the cart
   * page button, so a wiped code is put back before Shopify builds checkout.
   */
  installCheckoutGuard () {
    document.addEventListener('submit', (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return

      const submitter = event.submitter
      if (submitter?.getAttribute('name') !== 'checkout') return
      if (form.dataset.gwpCheckoutReady === 'true') {
        delete form.dataset.gwpCheckoutReady
        this.checkoutSubmitInFlight = false
        return
      }
      if (this.checkoutSubmitInFlight) {
        event.preventDefault()
        return
      }
      if (!this.pageShowsGift() && !this.discountCodeToRestore(this.state)) return

      this.checkoutSubmitInFlight = true
      event.preventDefault()
      event.stopPropagation()
      this.ensureDiscountBeforeCheckout(form, submitter)
    }, true)
  }

  /**
   * @param {RequestInfo|URL} input
   * @returns {boolean}
   */
  isCartUpdateRequest (input) {
    const raw = typeof input === 'string'
      ? input
      : input instanceof Request
        ? input.url
        : ''

    try {
      const path = new URL(raw, window.location.origin).pathname
      return /\/cart\/update(\.js)?$/.test(path)
    } catch (err) {
      return false
    }
  }

  /**
   * Attribute-only cart updates (Intelligems, Upstack) are applied as their
   * own cart write. Fired in parallel with the gift's discount update, that
   * write lands last and the code is gone, while the drawer still shows the
   * gift as free. Fold the code into the same write, and only put it back
   * afterwards when this request did not set `discount` itself — an explicit
   * `discount` is how a declined gift removes the code.
   *
   * @param {RequestInit} [init]
   * @returns {{ init: RequestInit|undefined, restoreIfStripped: boolean }}
   */
  prepareCartUpdate (init) {
    if (!init || typeof init.body !== 'string') {
      return { init, restoreIfStripped: false }
    }

    let payload
    try {
      payload = JSON.parse(init.body)
    } catch (err) {
      return { init, restoreIfStripped: false }
    }

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return { init, restoreIfStripped: false }
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'discount')) {
      return { init, restoreIfStripped: false }
    }

    const code = this.discountCodeToRestore(this.state)
    if (!code) return { init, restoreIfStripped: true }

    const current = this.getCurrentAppliedDiscountCodes(this.state)
    const next = current.includes(code) ? current : [...current, code]

    return {
      init: {
        ...init,
        body: JSON.stringify({
          ...payload,
          discount: next.join(','),
        }),
      },
      restoreIfStripped: true,
    }
  }

  /**
   * @param {Response} response
   */
  async restoreIfResponseStrippedDiscount (response) {
    let cart
    try {
      cart = await response.clone().json()
    } catch (err) {
      return
    }

    const code = this.discountCodeToRestore(cart)
    if (!code) return
    if ((cart.discount_codes || []).some((discount) => discount.code === code)) return

    await this.applyKeptDiscount(cart, code)
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement} submitter
   */
  async ensureDiscountBeforeCheckout (form, submitter) {
    try {
      const cart = await window.BAO.Cart.getCart()
      const code = this.discountCodeToRestore(cart)
      const present = code && (cart.discount_codes || []).some((discount) => discount.code === code)

      if (code && !present) {
        await this.applyKeptDiscount(cart, code)
      }
    } catch (err) {
      console.error('GWP: checkout discount check failed', err)
    }

    form.dataset.gwpCheckoutReady = 'true'

    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter)
        return
      }

      form.submit()
    } catch (err) {
      this.checkoutSubmitInFlight = false
      delete form.dataset.gwpCheckoutReady
      console.error('GWP: checkout submit failed', err)
    }
  }

  /**
   * @param {CartState} cart
   * @param {string} code
   */
  async applyKeptDiscount (cart, code) {
    const previous = this.restorePromise || Promise.resolve()
    const run = previous
      .catch(() => {})
      .then(() => this.writeKeptDiscount(cart, code))

    this.restorePromise = run.finally(() => {
      if (this.restorePromise === run) this.restorePromise = null
    })

    return this.restorePromise
  }

  /**
   * @param {CartState} cart
   * @param {string} code
   */
  async writeKeptDiscount (cart, code) {
    this.restoringDiscount = true

    try {
      const next = [
        ...this.getCurrentAppliedDiscountCodes(cart).filter((existing) => existing !== code),
        code,
      ]
      const state = await window.BAO.Cart.cartUpdate({
        discount: next.join(','),
      })

      if (state) this.replaceCartState(state)
    } catch (err) {
      console.error('GWP: failed to restore discount code', err)
    } finally {
      this.restoringDiscount = false
    }
  }

  /**
   * The discount code for a gift that is already in the cart. Threshold on
   * the rendered campaign JSON can still say false for a moment after the
   * line is added; the line itself is the signal that the code belongs there.
   *
   * @param {CartState|null|undefined} cart
   * @returns {string|null}
   */
  discountCodeToRestore (cart) {
    const claimedId = cart?.items?.find((item) => item.properties?._gwp === 'true')
      ?.properties?._gwp_campaign

    if (!claimedId) return null

    const campaign = this.campaigns.find((entry) => entry.campaignId === claimedId)
    if (!campaign?.discountCode || campaign.isActive === false) return null

    return campaign.discountCode
  }

  /**
   * @returns {boolean}
   */
  pageShowsGift () {
    return !!document.querySelector('[data-gwp-campaign]')
  }

  /**
   * Apply a GWP discount code once the customer has claimed a gift, or when
   * the current campaign is about to auto-add. A declined cycle must not
   * attach the first-in-block code.
   *
   * @returns {boolean}
   */
  shouldApplyCurrentDiscountCode () {
    if (!this.discountCode) return false
    if (this.isShareableLinkCampaignPrimary()) return true
    if (this.getClaimedCampaignId(this.state)) return true
    return this.shouldAutoAddCurrentCampaign()
  }

  /**
   * Clears per-cycle auto-add / auto-open flags when a campaign is no longer
   * eligible, and when the cart is empty, including when `shouldRun` is
   * false, so a later re-qualify in the same tab can auto-add again.
   */
  syncForegroundCampaignTracking () {
    if (!this.state || this.state.item_count === 0) {
      this.clearAllForegroundCycleTracking()
      return
    }

    for (const campaign of this.campaigns) {
      if (campaign.hiddenFromTheme) continue
      if (campaign.passedThreshold && campaign.isActive !== false) continue

      this.clearTrackedStateOfAutoAddedGwpItem(campaign.campaignId)
      this.clearTrackedStateOfAutoOpenedGwpModal(campaign.campaignId)
    }
  }

  clearAllForegroundCycleTracking () {
    for (const campaign of this.campaigns) {
      if (campaign.hiddenFromTheme) continue

      this.clearTrackedStateOfAutoAddedGwpItem(campaign.campaignId)
      this.clearTrackedStateOfAutoOpenedGwpModal(campaign.campaignId)
    }

    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (
        key?.startsWith('has_removed_auto_added_gwp_') ||
        key?.startsWith('has_opened_multi_gwp_modal_')
      ) {
        sessionStorage.removeItem(key)
      }
    }
  }

  /**
   * The campaign that all cart-mutating logic (auto-add, orphan cleanup,
   * discount-code management) acts on. A shareable-link campaign that has met
   * its qualifying criteria takes precedence over whatever the foreground
   * resolution picked.
   *
   * @returns {GwpCampaign}
   */
  get data () {
    if (this.state) {
      const shareableCampaign = this.getShareableLinkCampaign(this.state)
      if (shareableCampaign) return shareableCampaign
    }
    return this.getForegroundActiveCampaign() ?? { campaignId: null }
  }

  /**
   * True when the customer must pick a gift rather than one being
   * unambiguously auto-addable, either because multiple gift products are
   * offered, or because a single gift product has multiple variants.
   * `autoAddVariantId` is only ever set when exactly one product with exactly
   * one variant is offered.
   */
  get needsGiftSelection () {
    if (!this.shouldRun(this.state)) return false

    return this.data.autoAddVariantId == null && Number(this.data.productCount) > 0
  }

  /**
   * True when a shareable-link (background) campaign currently qualifies as
   * primary. It always wins over the foreground resolution and, having no
   * promotional UI of its own, is handled purely through silent auto-add.
   *
   * @returns {boolean}
   */
  isShareableLinkCampaignPrimary () {
    if (!this.state) return false

    return !!this.getShareableLinkCampaign(this.state)
  }

  /**
   * Manages the visibility of every visible (not hidden_from_theme) campaign's
   * DOM elements. Hidden campaigns never show a visible trigger or banner, so
   * there's nothing to manage for them here, since their gift is either
   * silently auto-added (`autoAddGwpItem`) or claimed via an auto-opened modal
   * (`maybeAutoOpenSelectionModal`).
   *
   * Visible campaigns are shown side by side until a gift is claimed, at which
   * point only the claimed campaign stays visible, unless a hidden
   * (shareable-link) campaign has taken over as primary, in which case all
   * visible campaigns are hidden. Declining the shareable-link gift removes its
   * discount code (see `declineShareableLinkGift`), which drops it out of
   * primary immediately and lets visible campaigns reappear here.
   */
  updateActiveCampaignVisibility () {
    const claimedCampaignId = this.getClaimedCampaignId(this.state)
    const shareableLinkIsPrimary = this.isShareableLinkCampaignPrimary()

    /** @param {HTMLElement} el @param {boolean} isVisible @param {boolean} isClaimedCampaign */
    const setVisibility = (el, isVisible, isClaimedCampaign) => {
      if (isVisible) {
        el.removeAttribute('data-gwp-inactive')
        el.style.removeProperty('display')

        // Only undo the disabling this function itself applies (hiding a
        // trigger because a different campaign was claimed). A claimed
        // campaign's own trigger is legitimately disabled elsewhere (Liquid /
        // `updateSelectionCounts`) because its gift is already in the cart,
        // so that must be left alone.
        if (el.hasAttribute('data-push-cart-gwp-trigger') && !isClaimedCampaign) {
          /** @type {HTMLButtonElement} */ (el).disabled = false
          el.removeAttribute('aria-disabled')
        }
        return
      }
      el.setAttribute('data-gwp-inactive', 'true')
      el.style.setProperty('display', 'none', 'important')
      if (el.hasAttribute('data-push-cart-gwp-trigger')) {
        /** @type {HTMLButtonElement} */ (el).disabled = true
        el.setAttribute('aria-disabled', 'true')
      }
    }

    const eligibleCampaignIds = new Set(
      this.getEligibleForegroundCampaigns().map((campaign) => campaign.campaignId),
    )
    const hasEligibleCampaign = eligibleCampaignIds.size > 0

    const foregroundCampaignIds = this.campaigns
      .filter((campaign) => !campaign.hiddenFromTheme)
      .map((campaign) => campaign.campaignId)

    for (const campaignId of foregroundCampaignIds) {
      const campaign = this.campaigns.find((item) => item.campaignId === campaignId)
      const isClaimedCampaign = campaignId === claimedCampaignId
      const isEligible = eligibleCampaignIds.has(campaignId)
      const isVisible =
        !shareableLinkIsPrimary &&
        campaign?.isActive !== false &&
        (
          claimedCampaignId
            ? isClaimedCampaign
            : hasEligibleCampaign
              ? isEligible
              : true
        )
      const campaignElements = /** @type {NodeListOf<HTMLElement>} */ (
        document.querySelectorAll(
          `.psh-CartItems_Gifts [data-gwp-campaign-id="${campaignId}"], .drw-Drawer-cart [data-gwp-campaign-id="${campaignId}"]`,
        )
      )
      campaignElements.forEach((el) => setVisibility(el, isVisible, isClaimedCampaign))
    }
  }
}

window.BAO.GiftWithPurchase = new GiftWithPurchase()

class GiftWithPurchaseItem extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-product-handle']
  }

  static get requiredElements () {
    return ['form', 'idInput', 'product-option']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this.els.form.element,
      'change',
      this.onFormChange.bind(this),
    )
  }

  /**
   * @param {Event} e
   */
  async onFormChange (e) {
    const select = /** @type {HTMLSelectElement} */ (e.target)

    const chosenOption = Array.from(select.options).find(
      (option) => option.value === select.value,
    )
    if (!chosenOption) {
      console.error(`Unable to find option for ${select.value}`)

      return
    }

    this.setIdInputValue(chosenOption.getAttribute('data-variant-id'))

    if (this.allValuesAreFilled) {
      const form = /** @type {HTMLFormElement} */ (this.els.form.element)
      return form.requestSubmit()
    }

    const productOptionElements = /** @type {HTMLSelectElement[]} */ (this.els['product-option'].elements)

    const productOptionValues = productOptionElements.map(
      (option) => {
        if (option.value === '') {
          return option.getAttribute('data-default-option-value-id')
        }

        const selectedOption = Array.from(option.options).find(
          (o) => o.value === option.value,
        )

        return selectedOption.getAttribute('data-option-id')
      },
    )

    const response = await window.BAO.sectionRenderer.fetchSingle(
      'product-gift-with-purchase-unclaimed',
      `/products/${this.getAttribute('data-product-handle')}?option_values=${productOptionValues.join(',')}`,
    )
    const newSection = new DOMParser().parseFromString(response, 'text/html')

    const newProductOptions = /** @type {HTMLSelectElement[]} */ (Array.from(
      newSection.querySelectorAll(this.els['product-option'].selector),
    ))

    for (const newProductOption of newProductOptions) {
      const currentProductOption = productOptionElements.find(
        (option) =>
          option.getAttribute('name') === newProductOption.getAttribute('name'),
      )
      if (!currentProductOption) continue

      for (let i = 0; i < currentProductOption.options.length; i++) {
        const currentOption = currentProductOption.options[i]
        const newOption = newProductOption.options[i]

        if (currentOption.value === '') continue

        currentOption.setAttribute(
          'data-variant-id',
          newOption.getAttribute('data-variant-id'),
        )

        if (!currentOption.hasAttribute('data-perma-disabled')) {
          currentOption.disabled = newOption.disabled
        }
      }
    }
  }

  /**
   * @param {string} value
   */
  setIdInputValue (value) {
    const idInput = /** @type {HTMLInputElement} */ (this.els.idInput.element)
    idInput.value = value
  }

  get allValuesAreFilled () {
    const productOptionElements = /** @type {HTMLSelectElement[]} */ (this.els['product-option'].elements)

    return productOptionElements.every((option) => {
      return option.value !== ''
    })
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define('gift-with-purchase-item', GiftWithPurchaseItem)
}

class GiftWithPurchasePDP extends HTMLElement {
  connectedCallback () {
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChange.bind(this),
    )
  }

  async onCartChange () {
    const section = /** @type {HTMLElement | null} */ (this.closest("[data-section-type='product-details']"))
    const sectionId = section?.dataset.sectionId
    if (!sectionId) return

    const response = await window.BAO.sectionRenderer.fetchSingle(
      sectionId,
      window.location.pathname,
    )

    const newDOM = window.BAO.sectionRenderer.convertToDOM(response)
    const newContent = newDOM.querySelector('gift-with-purchase-pdp')

    if (newContent) {
      this.innerHTML = newContent.innerHTML
    }
  }
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define('gift-with-purchase-pdp', GiftWithPurchasePDP)
}

class PushCartGWP {
  constructor () {
    /**
     * @property {string|null}
     * @public
     */
    this.activeModalIndex = null
    this.activeModalElement = null
    this.activeTriggerElement = null

    this.setupListeners()
    this.updateSelectionCounts()
  }

  /**
   * @param {string} index
   *
   * @returns {HTMLElement|null}
   */
  getModal (index) {
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-push-cart-gwp-modal="${index}"]`)
    )
  }

  /**
   * @param {string} index
   * @returns {HTMLButtonElement|null}
   */
  getTrigger (index) {
    return /** @type {HTMLButtonElement | null} */ (
      document.querySelector(`[data-push-cart-gwp-trigger="${index}"]`)
    )
  }

  /**
   * @returns {NodeListOf<HTMLButtonElement>}
   */
  get triggers () {
    return /** @type {NodeListOf<HTMLButtonElement>} */ (
      document.querySelectorAll('[data-push-cart-gwp-trigger]')
    )
  }

  /**
   * @returns {HTMLElement[]}
   */
  get modals () {
    return /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('[data-modal="gift-with-purchase"]'))
    )
  }

  /**
   * @param {HTMLButtonElement} trigger
   * @param {string|null} index
   * @param {string|null} campaignId
   *
   * @returns {HTMLElement|null}
   */
  getModalForTrigger (trigger, index, campaignId) {
    if (index) {
      const modalByIndex = this.getModal(index)
      if (modalByIndex) return modalByIndex
    }

    if (campaignId) {
      const modalByCampaign = /** @type {HTMLElement | null} */ (
        document.querySelector(
          `[data-modal="gift-with-purchase"][data-gwp-campaign-id="${campaignId}"]`,
        )
      )
      if (modalByCampaign) return modalByCampaign
    }

    const triggerPosition = Array.from(this.triggers).indexOf(trigger)
    const modalByPosition = this.modals[triggerPosition]
    if (modalByPosition) return modalByPosition

    return this.modals[0] || null
  }

  setupListeners () {
    document.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target)
      const trigger = /** @type {HTMLButtonElement | null} */ (target.closest('[data-push-cart-gwp-trigger]'))
      if (trigger && !trigger.disabled) {
        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        this.openModal(index)
        return
      }

      const closeBtn = target.closest('[data-push-cart-gwp-modal-close]')
      if (closeBtn) {
        this.closeModal()
        return
      }

      if (this.activeModalIndex != null) {
        const activeModal = this.getModal(this.activeModalIndex)
        if (activeModal && target === activeModal) {
          this.closeModal()
        }
      }
    })

    document.addEventListener('keydown', (e) => {
      const target = /** @type {Element} */ (e.target)
      const trigger = /** @type {HTMLButtonElement | null} */ (target.closest('[data-push-cart-gwp-trigger]'))
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        this.openModal(index)
        return
      }

      if (e.key === 'Escape' && this.activeModalIndex != null) {
        this.closeModal()
      }
    })

    document.addEventListener('submit', (e) => {
      const target = /** @type {Element} */ (e.target)
      const form = /** @type {HTMLFormElement | null} */ (target.closest('[data-gwp-form]'))
      if (form) {
        this.handleFormSubmit(e, form)
      }
    })

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_RENDER,
      (event) => this.updateSelectionCounts(event.detail?.state),
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.GIFT_WITH_PURCHASE.OPEN_MODAL,
      (event) => {
        const customEvent = /** @type {CustomEvent} */ (event)
        const campaignId = customEvent.detail?.campaignId
        if (!campaignId) return

        const trigger = Array.from(this.triggers).find((item) => {
          return item.getAttribute('data-gwp-campaign-id') === campaignId
        })

        if (!trigger) return

        if (trigger.disabled) return

        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        const modal = this.getModalForTrigger(trigger, index, campaignId)
        if (!modal) return

        this.openModalElement(modal, trigger)
      },
    )
  }

  /**
   * @param {SubmitEvent} e
   * @param {HTMLFormElement} form
   */
  async handleFormSubmit (e, form) {
    e.preventDefault()

    const submitButton = /** @type {HTMLButtonElement} */ (form.querySelector('button[type="submit"]'))

    if (submitButton.disabled) {
      return
    }

    submitButton.disabled = true
    submitButton.textContent = window.theme.strings.adding

    try {
      const formData = new FormData(form)
      const variantId = formData.get('id')
      const campaignId = formData.get('properties[_gwp_campaign]')
      await window.BAO.GiftWithPurchase.removeOneMatchingNonGwpItem(variantId)

      // The gift line itself would price correctly straight away now, but
      // `addItemFromForm` paints the footer too, and the subtotal still counts
      // the gift at full price until its discount code is applied. Keep this
      // one add waiting for the GWP pass so the totals never read high.
      const state = await window.BAO.Cart.addItemFromForm(form, {
        preventRender: true,
      })

      if (!state) {
        const current = await window.BAO.Cart.getCartWithSections()
        const giftWasAdded = window.BAO.GiftWithPurchase.hasItemInCart(
          current,
          campaignId,
        )

        if (giftWasAdded) {
          window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
            state: current,
            preventRender: true,
          })
        } else {
          window.BAO.Cart.syncRecommendationHistory(current.items)
          window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.RENDER, {
            state: current,
          })
          throw new Error('GWP: Failed to replace paid item')
        }
      }

      this.closeModal()

      setTimeout(() => {
        submitButton.disabled = false
        submitButton.textContent = window.theme.strings.add
      }, 1000)
    } catch (err) {
      console.error('GWP: Failed to add item', err)

      submitButton.disabled = false
      submitButton.textContent = window.theme.strings.add
    }
  }

  /**
   * @param {string} index
   */
  openModal (index) {
    const modal = this.getModal(index)
    if (!modal) return

    const trigger = this.getTrigger(index)
    this.openModalElement(modal, trigger)
  }

  /**
   * @param {HTMLElement} modal
   * @param {HTMLElement|null} trigger
   */
  openModalElement (modal, trigger = null) {
    if (!modal) return

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal)
    }

    this.activeModalIndex = modal.getAttribute('data-push-cart-gwp-modal')
    this.activeModalElement = modal
    this.activeTriggerElement = trigger

    modal.style.setProperty('display', 'flex', 'important')
    modal.style.setProperty('visibility', 'visible', 'important')
    modal.style.setProperty('opacity', '1', 'important')
    modal.classList.add('mod-Modal-active')
    modal.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'

    const firstInput = /** @type {HTMLElement | null} */ (modal.querySelector(
      'button, input, select, textarea',
    ))
    if (firstInput) {
      firstInput.focus()
    }
  }

  closeModal () {
    if (this.activeModalIndex == null && !this.activeModalElement) return

    const modal = this.activeModalElement || this.getModal(this.activeModalIndex)
    const trigger = this.activeTriggerElement || this.getTrigger(this.activeModalIndex)
    const campaignId = modal?.getAttribute('data-gwp-campaign-id')

    if (modal) {
      modal.classList.remove('mod-Modal-active')
      modal.setAttribute('aria-hidden', 'true')
      modal.style.removeProperty('display')
      modal.style.removeProperty('visibility')
      modal.style.removeProperty('opacity')
    }

    document.body.style.overflow = ''
    this.activeModalIndex = null
    this.activeModalElement = null
    this.activeTriggerElement = null

    if (trigger) {
      trigger.focus()
    }

    if (campaignId) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.GIFT_WITH_PURCHASE.MODAL_CLOSED, {
        campaignId,
      })
    }
  }

  /**
   * @param {CartState} [cartState] Cart state already held by the caller.
   *   Falls back to fetching the cart when not supplied (e.g. on first
   *   construction, before any cart state exists yet).
   */
  updateSelectionCounts (cartState) {
    if (!window.BAO.Cart) return

    const triggers = this.triggers
    if (triggers.length === 0) return

    const cartPromise = cartState ? Promise.resolve(cartState) : window.BAO.Cart.getCart()

    cartPromise.then((cart) => {
      triggers.forEach((trigger) => {
        if (trigger.hasAttribute('data-gwp-inactive')) return

        const index = trigger.getAttribute('data-push-cart-gwp-trigger')
        const campaignId = trigger.getAttribute('data-gwp-campaign-id')
        const modal = this.getModalForTrigger(trigger, index, campaignId)

        const gwpCount = cart.items.filter((item) => {
          return (
            item.properties &&
            item.properties._gwp === 'true' &&
            item.properties._gwp_campaign === campaignId
          )
        }).length

        const countElement = trigger.querySelector(
          '.psh-GiftWithPurchase_Selected',
        )
        const titleElement = trigger.querySelector(
          '.psh-GiftWithPurchase_Title',
        )
        const maxCount = Number(trigger.getAttribute('data-gwp-max-count')) || 1
        const selectedLabel = trigger.getAttribute('data-gwp-selected-label') || `${maxCount} selected`
        const defaultTitle = trigger.getAttribute('data-gwp-title-default') || 'Select Complimentary Gift'
        const selectedTitle = trigger.getAttribute('data-gwp-title-selected') || `${maxCount} complimentary free gift added`

        if (countElement) {
          countElement.textContent = `${gwpCount}/${selectedLabel}`
        }

        if (titleElement) {
          titleElement.textContent = gwpCount >= maxCount
            ? selectedTitle
            : defaultTitle
        }

        const submitButton = /** @type {HTMLButtonElement | null | undefined} */ (modal?.querySelector(
          '[data-gwp-form] button[type="submit"]',
        ))

        if (gwpCount >= maxCount) {
          trigger.disabled = true
          trigger.setAttribute('aria-disabled', 'true')
          trigger.classList.add('psh-GiftWithPurchase-disabled')

          if (submitButton) {
            submitButton.disabled = true
            submitButton.setAttribute('aria-disabled', 'true')
          }
        } else {
          trigger.disabled = false
          trigger.removeAttribute('aria-disabled')
          trigger.classList.remove('psh-GiftWithPurchase-disabled')

          if (submitButton) {
            submitButton.disabled = false
            submitButton.removeAttribute('aria-disabled')
          }
        }
      })
    })
  }
}

window.BAO = window.BAO || {}
window.BAO.PushCartGWP = new PushCartGWP()
